// src/app/app.routes.ts

import './reporting/report-block-registrations';

import { Routes } from '@angular/router';

import { authGuard } from './auth/auth.guard';
import {
  adminGuard,
  athleteGuard,
  athleteMatchGuard,
  coachGuard,
  coachMatchGuard,
  featureGuard,
  staffGuard,
  staffMatchGuard,
} from './auth/role.guard';
import { nutritionOnboardingGuard } from './nutrition/onboarding/onboarding.guard';
import { nutritionOnboardingDiscardGuard } from './nutrition/onboarding/onboarding-discard.guard';

// Route segments of keirin .. madison (track-competition.models BUNCH_ROUTE_SEGMENT; literal here to keep
// the models out of the main bundle).
const BUNCH_SEGMENTS = ['keirin', 'scratch', 'points-race', 'elimination', 'omnium', 'madison'];

const coachChildren: Routes = [
  // Dashboard (aggregierte Athleten-Übersicht) ist die Coach/Admin-Landing.
  { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
  {
    path: 'recovery',
    loadComponent: () =>
      import('./recovery/recovery-detail.component').then((m) => m.RecoveryDetailComponent),
  },
  {
    path: 'recovery/checkin',
    loadComponent: () =>
      import('./recovery/wellness-checkin-route.component').then((m) => m.WellnessCheckinRouteComponent),
  },
  {
    path: 'calendar',
    loadComponent: () =>
      import('./calendar/calendar.component').then((m) => m.CalendarComponent),
  },
  {
    path: 'analytics',
    loadComponent: () =>
      import('./analytics/analytics.component').then((m) => m.AnalyticsComponent),
  },
  {
    path: 'training-plans',
    canActivate: [featureGuard('training.plans', '/dashboard')],
    loadChildren: () =>
      import('./training-plans/training-plans.routes').then((m) => m.trainingPlansRoutes),
  },
  {
    path: 'athletes',
    loadComponent: () =>
      import('../app/coach/athletes/athletes-hub.component').then((m) => m.AthletesHubComponent),
  },
  {
    path: 'dashboard',
    loadComponent: () =>
      import('./coach/dashboard/dashboard-overview.component').then((m) => m.DashboardOverviewComponent),
  },
  {
    path: 'dashboard/athlete/:id',
    loadComponent: () =>
      import('./coach/dashboard/dashboard-athlete-detail.component').then((m) => m.DashboardAthleteDetailComponent),
  },
  {
    path: 'training-analysis/:id',
    loadComponent: () => {
      // ECharts parallel zum Route-Chunk anstossen: sonst startet der Download
      // (~350 kB gz) erst, wenn fit-data da ist und der Hauptgraph gebaut wird.
      // Gleicher Specifier wie NgxEchartsModule.forRoot in main.ts → derselbe Chunk.
      void import('echarts');
      return import('./training-analysis/training-analysis.component').then((m) => m.TrainingAnalysisComponent);
    },
  },
  {
    path: 'ki-insights',
    canActivate: [featureGuard('analysis.ki', '/dashboard')],
    loadComponent: () =>
      import('./athlete/analyse/ki-insights/ki-insights.component').then((m) => m.KiInsightsComponent),
  },
  // Trainingsbereiche bewusst als GESCHWISTER vor dem Diagnostik-Knoten und
  // nicht als dessen Kind: der Guard des Elternknotens ('analysis.diagnostics',
  // pro/premium) würde sonst Free-Nutzer wegleiten, obwohl der Kind-Key
  // 'analysis.diagnostics.zones' bewusst offener ist. Angular matcht in
  // Deklarationsreihenfolge, deshalb muss diese Route VOR dem Block stehen.
  // (Die Alternative — Guard vom Eltern- auf jeden Kindknoten verteilen —
  // wurde verworfen: ein vergessenes Kind wäre eine ungegatete Diagnostik.)
  {
    path: 'analyse/diagnostik/trainingsbereiche',
    canActivate: [featureGuard('analysis.diagnostics.zones', '/dashboard')],
    loadComponent: () =>
      import('./athlete/analyse/diagnostik/training-zones/training-zones-cockpit.component')
        .then((m) => m.TrainingZonesCockpitComponent),
  },
  {
    path: 'analyse/diagnostik',
    canActivate: [featureGuard('analysis.diagnostics', '/dashboard')],
    children: [
      {
        path: '',
        pathMatch: 'full',
        loadComponent: () =>
          import('./athlete/analyse/diagnostik/diagnostik.component').then((m) => m.DiagnostikComponent),
      },
      {
        path: 'test/new',
        loadComponent: () =>
          import('./athlete/analyse/diagnostik/cp-test-form/cp-test-form.component')
            .then((m) => m.CpTestFormComponent),
      },
      {
        path: 'test/:id',
        loadComponent: () =>
          import('./athlete/analyse/diagnostik/cp-test-detail/cp-test-detail.component')
            .then((m) => m.CpTestDetailComponent),
      },
      {
        path: 'test/:id/edit',
        loadComponent: () =>
          import('./athlete/analyse/diagnostik/cp-test-form/cp-test-form.component')
            .then((m) => m.CpTestFormComponent),
      },
      {
        path: 'kraft-geschwindigkeit',
        loadComponent: () =>
          import('./athlete/analyse/diagnostik/fv-profile/fv-profile-cockpit.component')
            .then((m) => m.FvProfileCockpitComponent),
      },
      {
        path: 'anthropometrie',
        loadComponent: () =>
          import('./athlete/analyse/diagnostik/anthropometry/anthropometry-cockpit.component')
            .then((m) => m.AnthropometryCockpitComponent),
      },
      {
        path: 'anthropometrie/neu',
        loadComponent: () =>
          import('./athlete/analyse/diagnostik/anthropometry/anthropometry-form.component')
            .then((m) => m.AnthropometryFormComponent),
      },
      {
        path: 'anthropometrie/:id',
        loadComponent: () =>
          import('./athlete/analyse/diagnostik/anthropometry/anthropometry-cockpit.component')
            .then((m) => m.AnthropometryCockpitComponent),
      },
      {
        path: 'anthropometrie/:id/bearbeiten',
        loadComponent: () =>
          import('./athlete/analyse/diagnostik/anthropometry/anthropometry-form.component')
            .then((m) => m.AnthropometryFormComponent),
      },
      {
        path: 'blutbild',
        loadComponent: () =>
          import('./athlete/analyse/diagnostik/blood-lab/blood-lab-list.component')
            .then((m) => m.BloodLabListComponent),
      },
      {
        path: 'bioimpedanz',
        loadComponent: () =>
          import('./athlete/analyse/diagnostik/bioimpedance/bia-cockpit.component')
            .then((m) => m.BiaCockpitComponent),
      },
      {
        path: 'bioimpedanz/neu',
        loadComponent: () =>
          import('./athlete/analyse/diagnostik/bioimpedance/bia-measurement-form.component')
            .then((m) => m.BiaMeasurementFormComponent),
      },
      {
        path: 'bioimpedanz/:id/bearbeiten',
        loadComponent: () =>
          import('./athlete/analyse/diagnostik/bioimpedance/bia-measurement-form.component')
            .then((m) => m.BiaMeasurementFormComponent),
      },
    ],
  },
  { path: 'diagnostik', redirectTo: 'analyse/diagnostik', pathMatch: 'full' },
  {
    path: 'nutrition',
    children: [
      // The diary is the nutrition landing: athletes come here to read their
      // day, not to log a product. Logging is reached from the "+" quick
      // actions (athlete-layout `baseQuickActions`) and per meal from the diary.
      { path: '', redirectTo: 'log', pathMatch: 'full' },
      // Legacy: /nutrition/scanner is consolidated into /nutrition/search
      // (the barcode scanner lives inside the search page). Redirect keeps
      // bookmarks / PWA shortcuts working instead of 404-ing.
      { path: 'scanner', redirectTo: 'search', pathMatch: 'full' },
      {
        // Real nutrition diary / evaluation view (free). Reclaims the former
        // log→search redirect; surfaces logged consumption + macro targets.
        path: 'log',
        canActivate: [nutritionOnboardingGuard],
        loadComponent: () =>
          import('./nutrition/diary/nutrition-diary.component').then((m) => m.NutritionDiaryComponent),
      },
      {
        path: 'search',
        canActivate: [nutritionOnboardingGuard],
        loadComponent: () =>
          import('./nutrition/manual-search/manual-search.component').then((m) => m.ManualSearchComponent),
      },
      {
        path: 'recipes',
        canActivate: [nutritionOnboardingGuard],
        loadComponent: () =>
          import('./nutrition/recipes/recipes.component').then((m) => m.RecipesComponent),
      },
      {
        // No featureGuard: component renders its own locked-overlay teaser for
        // Free users (nutrition.planning is Pro+).
        path: 'plan',
        canActivate: [nutritionOnboardingGuard],
        loadComponent: () =>
          import('./nutrition/meal-planning/meal-planning.component').then((m) => m.MealPlanningComponent),
      },
      {
        // No featureGuard: component renders its own locked-overlay teaser for
        // Free users (nutrition.analysis is Pro+) — same pattern as 'plan'.
        // A guard would bounce a Free user back to the diary without ever
        // telling them what they are missing.
        path: 'analysis',
        canActivate: [nutritionOnboardingGuard],
        loadComponent: () =>
          import('./nutrition/evaluation/nutrition-evaluation.component').then(
            (m) => m.NutritionEvaluationComponent,
          ),
      },
      {
        path: 'onboarding',
        canDeactivate: [nutritionOnboardingDiscardGuard],
        loadComponent: () =>
          import('./nutrition/onboarding/nutrition-onboarding.component'),
      },
      {
        path: 'add',
        canActivate: [nutritionOnboardingGuard],
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./nutrition/add/add-landing/add-landing.component').then((m) => m.AddLandingComponent),
          },
          {
            path: 'product',
            loadComponent: () =>
              import('./nutrition/add/add-product/add-product.component').then((m) => m.AddProductComponent),
          },
          {
            path: 'recipe',
            loadComponent: () =>
              import('./nutrition/add/add-recipe/add-recipe.component').then((m) => m.AddRecipeComponent),
          },
        ],
      },
    ],
  },
  {
    path: 'report',
    loadComponent: () =>
      import('./reporting/report-composer.component').then((m) => m.ReportComposerComponent),
  },
  {
    path: 'report/vergleich',
    loadComponent: () =>
      import('./analytics/phase-comparison/phase-comparison.component').then((m) => m.PhaseComparisonComponent),
  },
  {
    path: 'report/entwicklung',
    canActivate: [featureGuard('reports.pdf.development', '/report')],
    loadComponent: () =>
      import('./analytics/development-report/development-report.component').then((m) => m.DevelopmentReportComponent),
  },
  {
    path: 'tools/team-pursuit',
    canActivate: [featureGuard('tools.team_pursuit', '/dashboard')],
    loadComponent: () =>
      import('./tools/team-pursuit/team-pursuit.component').then((m) => m.TeamPursuitComponent),
  },
  // Bahn-Wettkaempfe (13.10.2026): Wettkampf -> Disziplinen -> Event-Seite der
  // Disziplin. Die alten Event-Pfade der beiden Tools leiten hierher um.
  {
    path: 'tools/track-competitions',
    canActivate: [featureGuard('tools.track_competitions', '/dashboard')],
    loadComponent: () =>
      import('./tools/track-competitions/track-competitions.component').then((m) => m.TrackCompetitionsComponent),
  },
  // Sprint (Einzelsprint, 15.10.2026): Disziplin-Seite + Analyse ueber Wettkaempfe
  {
    path: 'tools/track-competitions/sprint-analyse',
    canActivate: [featureGuard('tools.track_competitions', '/dashboard')],
    loadComponent: () =>
      import('./tools/track-competitions/sprint/sprint-analysis-page.component')
        .then((m) => m.SprintAnalysisPageComponent),
  },
  // Altersstruktur der Weltmeisterschaften (20.10.2026, Variante A)
  {
    path: 'tools/track-competitions/altersstruktur',
    canActivate: [featureGuard('tools.track_competitions', '/dashboard')],
    loadComponent: () =>
      import('./tools/track-competitions/age-structure/age-structure-page.component')
        .then((m) => m.AgeStructurePageComponent),
  },
  // Laenderwertung der Weltmeisterschaften (23.10.2026, Variante B + Details C)
  {
    path: 'tools/track-competitions/laenderwertung',
    canActivate: [featureGuard('tools.track_competitions', '/dashboard')],
    loadComponent: () =>
      import('./tools/track-competitions/nation-ranking/nation-ranking-page.component')
        .then((m) => m.NationRankingPageComponent),
  },
  // Leistungsentwicklung (24.10.2026, vorher "Siegerzeiten"): Reiter-Huelle, je Reiter eine Kind-Route
  // (Reihenfolge = PERFORMANCE_TABS). Siegerzeiten WM + OS mit Olympia-Prognose (23.10.2026, Variante C),
  // Massenstart (Durchschnittstempo) + MV im Vergleich (24.10.2026, Variante C + Rechner aus B), Rueckstand Deutschland (24.10.2026, Variante B).
  { path: 'tools/track-competitions/siegerzeiten', redirectTo: 'tools/track-competitions/leistungsentwicklung/siegerzeiten', pathMatch: 'full' },
  {
    path: 'tools/track-competitions/leistungsentwicklung',
    canActivate: [featureGuard('tools.track_competitions', '/dashboard')],
    loadComponent: () =>
      import('./tools/track-competitions/performance/performance-page.component')
        .then((m) => m.PerformancePageComponent),
    children: [
      { path: '', redirectTo: 'siegerzeiten', pathMatch: 'full' },
      {
        path: 'siegerzeiten',
        loadComponent: () =>
          import('./tools/track-competitions/winning-times/winning-times-page.component')
            .then((m) => m.WinningTimesPageComponent),
      },
      {
        path: 'massenstart',
        loadComponent: () =>
          import('./tools/track-competitions/mass-start/mass-start-page.component')
            .then((m) => m.MassStartPageComponent),
      },
      {
        path: 'mv-vergleich',
        loadComponent: () =>
          import('./tools/track-competitions/performance-comparison/performance-comparison-page.component')
            .then((m) => m.PerformanceComparisonPageComponent),
      },
      {
        path: 'rueckstand-deutschland',
        loadComponent: () =>
          import('./tools/track-competitions/nation-gap/nation-gap-page.component')
            .then((m) => m.NationGapPageComponent),
      },
    ],
  },
  {
    path: 'tools/track-competitions/sprint/:eventId',
    canActivate: [featureGuard('tools.track_competitions', '/dashboard')],
    loadComponent: () =>
      import('./tools/track-competitions/sprint/sprint-event-detail.component')
        .then((m) => m.SprintEventDetailComponent),
  },
  // Head-to-Head der Laeufe (16.10.2026, ?a=run:12&b=run:34..&h=) -- vor ':eventId',
  // sonst wuerde 'vergleich' als Event-ID gelesen.
  {
    path: 'tools/track-competitions/time-trial/vergleich',
    canActivate: [featureGuard('tools.track_competitions', '/dashboard')],
    data: { h2h: 'time_trial' },
    loadComponent: () =>
      import('./tools/team-pursuit/head-to-head/head-to-head-page.component')
        .then((m) => m.HeadToHeadPageComponent),
  },
  {
    path: 'tools/track-competitions/individual-pursuit/vergleich',
    canActivate: [featureGuard('tools.track_competitions', '/dashboard')],
    data: { h2h: 'individual_pursuit' },
    loadComponent: () =>
      import('./tools/team-pursuit/head-to-head/head-to-head-page.component')
        .then((m) => m.HeadToHeadPageComponent),
  },
  // 1 km Zeitfahren + Einerverfolgung (16.10.2026): eine Seite fuer beide, Zeiten aus der Race Analysis
  {
    path: 'tools/track-competitions/time-trial/:eventId',
    canActivate: [featureGuard('tools.track_competitions', '/dashboard')],
    loadComponent: () =>
      import('./tools/track-competitions/individual/individual-event-detail.component')
        .then((m) => m.IndividualEventDetailComponent),
  },
  {
    path: 'tools/track-competitions/individual-pursuit/:eventId',
    canActivate: [featureGuard('tools.track_competitions', '/dashboard')],
    loadComponent: () =>
      import('./tools/track-competitions/individual/individual-event-detail.component')
        .then((m) => m.IndividualEventDetailComponent),
  },
  // Keirin, Scratch, Punktefahren, Ausscheidung, Omnium, Madison (20.10.2026): eine Seite fuer alle sechs,
  // Daten nur aus dem Result Book der Wettkampfseite
  ...BUNCH_SEGMENTS.map((segment) => ({
    path: `tools/track-competitions/${segment}/:eventId`,
    canActivate: [featureGuard('tools.track_competitions', '/dashboard')],
    loadComponent: () =>
      import('./tools/track-competitions/bunch/bunch-event-detail.component')
        .then((m) => m.BunchEventDetailComponent),
  })),
  {
    path: 'tools/track-competitions/team-pursuit/:eventId',
    canActivate: [featureGuard('tools.track_competitions', '/dashboard')],
    loadComponent: () =>
      import('./tools/track-competitions/team-pursuit/pursuit-event-detail/pursuit-event-detail.component')
        .then((m) => m.PursuitEventDetailComponent),
  },
  {
    path: 'tools/track-competitions/team-sprint/:eventId',
    canActivate: [featureGuard('tools.track_competitions', '/dashboard')],
    loadComponent: () =>
      import('./tools/track-competitions/team-sprint/event-detail/event-detail.component')
        .then((m) => m.EventDetailComponent),
  },
  // Wettkampf-Berichte (24.10.2026): Vorlagen, Textbausteine, Editor -- nur Bearbeiter
  {
    path: 'tools/track-competitions/berichte/vorlagen',
    canActivate: [featureGuard('tools.track_competitions.edit', '/tools/track-competitions')],
    loadComponent: () =>
      import('./tools/track-competitions/report/report-templates-page.component')
        .then((m) => m.ReportTemplatesPageComponent),
  },
  {
    path: 'tools/track-competitions/berichte/bausteine',
    canActivate: [featureGuard('tools.track_competitions.edit', '/tools/track-competitions')],
    loadComponent: () =>
      import('./tools/track-competitions/report/report-snippets-page.component')
        .then((m) => m.ReportSnippetsPageComponent),
  },
  {
    path: 'tools/track-competitions/:competitionId/bericht/:reportId',
    canActivate: [featureGuard('tools.track_competitions.edit', '/tools/track-competitions')],
    loadComponent: () =>
      import('./tools/track-competitions/report/report-editor.component')
        .then((m) => m.ReportEditorComponent),
  },
  {
    path: 'tools/track-competitions/:competitionId',
    canActivate: [featureGuard('tools.track_competitions', '/dashboard')],
    loadComponent: () =>
      import('./tools/track-competitions/competition-detail/competition-detail.component')
        .then((m) => m.CompetitionDetailComponent),
  },
  { path: 'tools/team-pursuit/event/:eventId', redirectTo: 'tools/track-competitions/team-pursuit/:eventId' },
  { path: 'tools/team-sprint/:eventId', redirectTo: 'tools/track-competitions/team-sprint/:eventId' },
  {
    // Head-to-head of two Wegzeitverlaeufe (?a=effort:12&b=heat:34) -- before
    // ':effortId', or 'vergleich' would be read as an effort id.
    path: 'tools/team-pursuit/vergleich',
    canActivate: [featureGuard('tools.team_pursuit', '/dashboard')],
    loadComponent: () =>
      import('./tools/team-pursuit/head-to-head/head-to-head-page.component')
        .then((m) => m.HeadToHeadPageComponent),
  },
  {
    path: 'tools/team-pursuit/:effortId',
    canActivate: [featureGuard('tools.team_pursuit', '/dashboard')],
    loadComponent: () =>
      import('./tools/team-pursuit/effort-detail/effort-detail.component').then((m) => m.EffortDetailComponent),
  },
  // Einzelbelastungen (PLAN_INDIVIDUAL_EFFORTS): same list and detail page as
  // Team Pursuit on the discipline 'individual' -- tier feature (premium,
  // coaches via role), not a flag. The backend checks the discipline per effort.
  {
    path: 'tools/individual-efforts',
    canActivate: [featureGuard('tools.individual_efforts', '/dashboard')],
    data: { discipline: 'individual' },
    loadComponent: () =>
      import('./tools/team-pursuit/team-pursuit.component').then((m) => m.TeamPursuitComponent),
  },
  {
    path: 'tools/individual-efforts/:effortId',
    canActivate: [featureGuard('tools.individual_efforts', '/dashboard')],
    data: { discipline: 'individual' },
    loadComponent: () =>
      import('./tools/team-pursuit/effort-detail/effort-detail.component').then((m) => m.EffortDetailComponent),
  },
  {
    // Kuratierwerkzeug fuer die ML-Grundwahrheit. Flag-gated, nicht tarif-
    // gebunden: was hier etikettiert wird, ist Trainingsziel des Detektors.
    path: 'tools/lap-labeling',
    canActivate: [featureGuard('tools.lap_labeling', '/dashboard')],
    loadComponent: () =>
      import('./tools/lap-labeling/lap-labeling.component').then((m) => m.LapLabelingComponent),
  },
  {
    path: 'tools/team-sprint',
    canActivate: [featureGuard('tools.team_sprint', '/dashboard')],
    loadComponent: () =>
      import('./tools/team-sprint/team-sprint.component').then((m) => m.TeamSprintComponent),
  },
  {
    path: 'tools/team-lactate',
    canActivate: [featureGuard('tools.team_lactate', '/dashboard')],
    loadComponent: () =>
      import('./tools/team-lactate/team-lactate.component').then((m) => m.TeamLactateComponent),
  },
  {
    path: 'tools/sensors',
    canActivate: [featureGuard('tools.sensors', '/dashboard')],
    loadComponent: () =>
      import('./tools/sensors/sensors.component').then((m) => m.SensorsComponent),
  },
  {
    path: 'tools/aero-testing',
    canActivate: [featureGuard('tools.aero_testing', '/dashboard')],
    loadComponent: () =>
      import('./tools/aero-testing/aero-testing.component').then((m) => m.AeroTestingComponent),
  },
  {
    path: 'tools/aero-testing/:sessionId',
    canActivate: [featureGuard('tools.aero_testing', '/dashboard')],
    loadComponent: () =>
      import('./tools/aero-testing/session-detail/session-detail.component')
        .then((m) => m.SessionDetailComponent),
  },
  {
    path: 'tools/pm-validation',
    canActivate: [featureGuard('tools.pm_validation', '/dashboard')],
    loadComponent: () =>
      import('./tools/pm-validation/pm-validation.component').then((m) => m.PmValidationComponent),
  },
  {
    path: 'tools/lab-import',
    canActivate: [featureGuard('tools.lab_import', '/dashboard')],
    loadComponent: () =>
      import('./tools/lab-import/lab-import.component').then((m) => m.LabImportComponent),
  },
  {
    // Laktat-Direkteingabe (Tablet) — Kind von tools.lab_import, erbt denselben Tier/Guard.
    path: 'tools/lab-import/lactate',
    canActivate: [featureGuard('tools.lab_import', '/dashboard')],
    loadComponent: () =>
      import('./tools/lab-import/lactate-entry/lactate-entry.component').then((m) => m.LactateEntryComponent),
  },
  {
    // Kontoverwaltung (Passwort + Anmelde-E-Mail). MUSS in coachChildren UND
    // athleteChildren stehen — die beiden Layouts haben getrennte Routenbäume,
    // und /profil liegt ausschliesslich im Athleten-Baum. Ohne den Eintrag
    // hier könnte ein Coach sein Passwort nicht ändern.
    path: 'account',
    loadComponent: () =>
      import('./account/account.component').then((m) => m.AccountComponent),
  },
  {
    path: 'user_settings',
    loadComponent: () =>
      import('./user_settings/user_settings.component').then((m) => m.SettingsComponent),
  },
  {
    path: 'integrations',
    loadComponent: () =>
      import('./integrations/integrations.component').then((m) => m.IntegrationsComponent),
  },
  {
    path: 'athleten',
    children: [
      {
        path: '',
        pathMatch: 'full',
        loadComponent: () =>
          import('./athletes/athletes-list.component').then((m) => m.AthletesListComponent),
      },
      {
        path: 'neu',
        canActivate: [adminGuard],
        loadComponent: () =>
          import('./athletes/athlete-create.component').then((m) => m.AthleteCreateComponent),
      },
      {
        path: ':id',
        loadComponent: () =>
          import('./athletes/athlete-detail.component').then((m) => m.AthleteDetailComponent),
      },
    ],
  },
  {
    path: 'admin',
    canActivate: [adminGuard],
    children: [
      { path: '', redirectTo: 'overview', pathMatch: 'full' },
      {
        path: 'overview',
        loadComponent: () =>
          import('./admin/overview/admin-overview.component').then((m) => m.AdminOverviewComponent),
      },
      {
        path: 'users',
        loadComponent: () =>
          import('./admin/users/user-list.component').then((m) => m.UserListComponent),
      },
      {
        path: 'users/new',
        loadComponent: () =>
          import('./admin/users/user-form.component').then((m) => m.UserFormComponent),
      },
      {
        path: 'users/:id',
        loadComponent: () =>
          import('./admin/users/user-detail.component').then((m) => m.UserDetailComponent),
      },
      {
        path: 'associations',
        loadComponent: () =>
          import('./admin/associations/association-list.component').then((m) => m.AssociationListComponent),
      },
      {
        path: 'workspaces',
        loadComponent: () =>
          import('./admin/workspaces/workspaces-list.component').then((m) => m.WorkspacesListComponent),
      },
      {
        path: 'workspaces/:id',
        loadComponent: () =>
          import('./admin/workspaces/workspace-detail.component').then((m) => m.WorkspaceDetailComponent),
      },
      {
        path: 'groups',
        loadComponent: () =>
          import('./admin/groups/groups-list.component').then((m) => m.GroupsListComponent),
      },
      {
        path: 'groups/:id',
        loadComponent: () =>
          import('./admin/groups/group-detail.component').then((m) => m.GroupDetailComponent),
      },
      {
        path: 'staff',
        loadComponent: () =>
          import('./admin/staff/staff-admin.component').then((m) => m.StaffAdminComponent),
      },
      {
        path: 'blood-units',
        loadComponent: () =>
          import('./admin/blood-units/blood-units.component').then((m) => m.BloodUnitsAdminComponent),
      },
      {
        // workouts.csv -> title/description/comments/RPE of one athlete's
        // sessions; athlete picked on the page (never the athlete switcher).
        path: 'trainingpeaks-import',
        loadComponent: () =>
          import('./admin/trainingpeaks-import/trainingpeaks-import-admin.component')
            .then((m) => m.TrainingpeaksImportAdminComponent),
      },
    ],
  },
  { path: '**', redirectTo: 'dashboard' },
];

const athleteChildren: Routes = [
  { path: '', redirectTo: 'today', pathMatch: 'full' },
  {
    path: 'today',
    loadComponent: () =>
      import('./athlete/today/today.component').then((m) => m.TodayComponent),
  },
  // Backwards-compat: alte /heute-URL umleiten auf /today
  { path: 'heute', redirectTo: 'today', pathMatch: 'full' },
  {
    path: 'planen',
    children: [
      {
        path: '',
        pathMatch: 'full',
        loadComponent: () =>
          import('./athlete/planen/planen.component').then((m) => m.PlanenComponent),
      },
      {
        path: 'kalender',
        loadComponent: () =>
          import('./calendar/calendar.component').then((m) => m.CalendarComponent),
      },
      {
        path: 'plans',
        canActivate: [featureGuard('training.plans', '/planen')],
        loadComponent: () =>
          import('./training-plans/plan-builder/plan-builder.component').then((m) => m.PlanBuilderComponent),
      },
      {
        path: 'plaene',
        redirectTo: 'plans',
        pathMatch: 'full',
      },
      {
        path: 'builder',
        canActivate: [featureGuard('training.builder', '/planen')],
        loadComponent: () =>
          import('./training-plans/workout-builder/workout-builder.component').then((m) => m.WorkoutBuilderComponent),
      },
      {
        path: 'saison',
        canActivate: [featureGuard('training.season', '/planen')],
        loadComponent: () =>
          import('./training-plans/season-planning/season-planning.component').then((m) => m.SeasonPlanningComponent),
      },
      {
        path: 'bibliothek',
        loadComponent: () =>
          import('./training-plans/workout-library/workout-library.component').then((m) => m.WorkoutLibraryComponent),
      },
    ],
  },
  {
    path: 'analyse',
    loadComponent: () =>
      import('./athlete/analyse/analyse.component').then((m) => m.AnalyseComponent),
    children: [
      { path: '', redirectTo: 'trends', pathMatch: 'full' },
      {
        path: 'trends',
        loadComponent: () =>
          import('./analytics/analytics.component').then((m) => m.AnalyticsComponent),
      },
      {
        path: 'detail',
        redirectTo: 'trends',
        pathMatch: 'full',
      },
      {
        path: 'detail/:id',
        loadComponent: () => {
          // ECharts vorladen — siehe 'training-analysis/:id'.
          void import('echarts');
          return import('./training-analysis/training-analysis.component').then((m) => m.TrainingAnalysisComponent);
        },
      },
      {
        path: 'reports',
        loadComponent: () =>
          import('./reporting/report-composer.component').then((m) => m.ReportComposerComponent),
      },
      {
        path: 'entwicklung',
        canActivate: [featureGuard('reports.pdf.development', '/analyse/trends')],
        loadComponent: () =>
          import('./analytics/development-report/development-report.component').then((m) => m.DevelopmentReportComponent),
      },
      {
        path: 'ki',
        canActivate: [featureGuard('analysis.ki', '/analyse/trends')],
        loadComponent: () =>
          import('./athlete/analyse/ki-insights/ki-insights.component').then((m) => m.KiInsightsComponent),
      },
      // Siehe Kommentar im Coach-Zweig: Geschwister VOR dem gegateten Knoten,
      // damit der offenere Kind-Key nicht vom Eltern-Guard überstimmt wird.
      {
        path: 'diagnostik/trainingsbereiche',
        canActivate: [featureGuard('analysis.diagnostics.zones', '/analyse/trends')],
        loadComponent: () =>
          import('./athlete/analyse/diagnostik/training-zones/training-zones-cockpit.component')
            .then((m) => m.TrainingZonesCockpitComponent),
      },
      {
        path: 'diagnostik',
        canActivate: [featureGuard('analysis.diagnostics', '/analyse/trends')],
        children: [
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () =>
              import('./athlete/analyse/diagnostik/diagnostik.component').then((m) => m.DiagnostikComponent),
          },
          {
            path: 'test/new',
            loadComponent: () =>
              import('./athlete/analyse/diagnostik/cp-test-form/cp-test-form.component')
                .then((m) => m.CpTestFormComponent),
          },
          {
            path: 'test/:id',
            loadComponent: () =>
              import('./athlete/analyse/diagnostik/cp-test-detail/cp-test-detail.component')
                .then((m) => m.CpTestDetailComponent),
          },
          {
            path: 'test/:id/edit',
            loadComponent: () =>
              import('./athlete/analyse/diagnostik/cp-test-form/cp-test-form.component')
                .then((m) => m.CpTestFormComponent),
          },
          {
            path: 'kraft-geschwindigkeit',
            loadComponent: () =>
              import('./athlete/analyse/diagnostik/fv-profile/fv-profile-cockpit.component')
                .then((m) => m.FvProfileCockpitComponent),
          },
          {
            path: 'anthropometrie',
            loadComponent: () =>
              import('./athlete/analyse/diagnostik/anthropometry/anthropometry-cockpit.component')
                .then((m) => m.AnthropometryCockpitComponent),
          },
          {
            path: 'anthropometrie/neu',
            loadComponent: () =>
              import('./athlete/analyse/diagnostik/anthropometry/anthropometry-form.component')
                .then((m) => m.AnthropometryFormComponent),
          },
          {
            path: 'anthropometrie/:id',
            loadComponent: () =>
              import('./athlete/analyse/diagnostik/anthropometry/anthropometry-cockpit.component')
                .then((m) => m.AnthropometryCockpitComponent),
          },
          {
            path: 'anthropometrie/:id/bearbeiten',
            loadComponent: () =>
              import('./athlete/analyse/diagnostik/anthropometry/anthropometry-form.component')
                .then((m) => m.AnthropometryFormComponent),
          },
          {
            path: 'blutbild',
            loadComponent: () =>
              import('./athlete/analyse/diagnostik/blood-lab/blood-lab-list.component')
                .then((m) => m.BloodLabListComponent),
          },
          {
            path: 'bioimpedanz',
            loadComponent: () =>
              import('./athlete/analyse/diagnostik/bioimpedance/bia-cockpit.component')
                .then((m) => m.BiaCockpitComponent),
          },
          {
            path: 'bioimpedanz/neu',
            loadComponent: () =>
              import('./athlete/analyse/diagnostik/bioimpedance/bia-measurement-form.component')
                .then((m) => m.BiaMeasurementFormComponent),
          },
          {
            path: 'bioimpedanz/:id/bearbeiten',
            loadComponent: () =>
              import('./athlete/analyse/diagnostik/bioimpedance/bia-measurement-form.component')
                .then((m) => m.BiaMeasurementFormComponent),
          },
        ],
      },
    ],
  },
  {
    // Zwilling des Eintrags in coachChildren — siehe dortigen Kommentar.
    path: 'account',
    loadComponent: () =>
      import('./account/account.component').then((m) => m.AccountComponent),
  },
  {
    path: 'profil',
    children: [
      {
        path: '',
        pathMatch: 'full',
        loadComponent: () =>
          import('./athlete/profil/profil.component').then((m) => m.ProfilComponent),
      },
      {
        path: 'einstellungen',
        loadComponent: () =>
          import('./user_settings/user_settings.component').then((m) => m.SettingsComponent),
      },
      {
        path: 'integrationen',
        loadComponent: () =>
          import('./integrations/integrations.component').then((m) => m.IntegrationsComponent),
      },
    ],
  },
  {
    path: 'nutrition',
    children: [
      // The diary is the nutrition landing: athletes come here to read their
      // day, not to log a product. Logging is reached from the "+" quick
      // actions (athlete-layout `baseQuickActions`) and per meal from the diary.
      { path: '', redirectTo: 'log', pathMatch: 'full' },
      // Legacy: /nutrition/scanner is consolidated into /nutrition/search
      // (the barcode scanner lives inside the search page). Redirect keeps
      // bookmarks / PWA shortcuts working instead of 404-ing.
      { path: 'scanner', redirectTo: 'search', pathMatch: 'full' },
      {
        // Real nutrition diary / evaluation view (free). Reclaims the former
        // log→search redirect; surfaces logged consumption + macro targets.
        path: 'log',
        canActivate: [nutritionOnboardingGuard],
        loadComponent: () =>
          import('./nutrition/diary/nutrition-diary.component').then((m) => m.NutritionDiaryComponent),
      },
      {
        path: 'search',
        canActivate: [nutritionOnboardingGuard],
        loadComponent: () =>
          import('./nutrition/manual-search/manual-search.component').then((m) => m.ManualSearchComponent),
      },
      {
        path: 'recipes',
        canActivate: [nutritionOnboardingGuard],
        loadComponent: () =>
          import('./nutrition/recipes/recipes.component').then((m) => m.RecipesComponent),
      },
      {
        // No featureGuard: component renders its own locked-overlay teaser for
        // Free users (nutrition.planning is Pro+).
        path: 'plan',
        canActivate: [nutritionOnboardingGuard],
        loadComponent: () =>
          import('./nutrition/meal-planning/meal-planning.component').then((m) => m.MealPlanningComponent),
      },
      {
        // No featureGuard: component renders its own locked-overlay teaser for
        // Free users (nutrition.analysis is Pro+) — same pattern as 'plan'.
        // A guard would bounce a Free user back to the diary without ever
        // telling them what they are missing.
        path: 'analysis',
        canActivate: [nutritionOnboardingGuard],
        loadComponent: () =>
          import('./nutrition/evaluation/nutrition-evaluation.component').then(
            (m) => m.NutritionEvaluationComponent,
          ),
      },
      {
        path: 'onboarding',
        canDeactivate: [nutritionOnboardingDiscardGuard],
        loadComponent: () =>
          import('./nutrition/onboarding/nutrition-onboarding.component'),
      },
      {
        path: 'add',
        canActivate: [nutritionOnboardingGuard],
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./nutrition/add/add-landing/add-landing.component').then((m) => m.AddLandingComponent),
          },
          {
            path: 'product',
            loadComponent: () =>
              import('./nutrition/add/add-product/add-product.component').then((m) => m.AddProductComponent),
          },
          {
            path: 'recipe',
            loadComponent: () =>
              import('./nutrition/add/add-recipe/add-recipe.component').then((m) => m.AddRecipeComponent),
          },
        ],
      },
    ],
  },
  {
    path: 'recovery',
    loadComponent: () =>
      import('./recovery/recovery-detail.component').then((m) => m.RecoveryDetailComponent),
  },
  {
    path: 'recovery/checkin',
    loadComponent: () =>
      import('./recovery/wellness-checkin-route.component').then((m) => m.WellnessCheckinRouteComponent),
  },
  {
    path: 'tools/team-pursuit',
    canActivate: [featureGuard('tools.team_pursuit', '/today')],
    loadComponent: () =>
      import('./tools/team-pursuit/team-pursuit.component').then((m) => m.TeamPursuitComponent),
  },
  {
    path: 'tools/sensors',
    canActivate: [featureGuard('tools.sensors', '/today')],
    loadComponent: () =>
      import('./tools/sensors/sensors.component').then((m) => m.SensorsComponent),
  },
  {
    path: 'tools/aero-testing',
    canActivate: [featureGuard('tools.aero_testing', '/today')],
    loadComponent: () =>
      import('./tools/aero-testing/aero-testing.component').then((m) => m.AeroTestingComponent),
  },
  {
    path: 'tools/aero-testing/:sessionId',
    canActivate: [featureGuard('tools.aero_testing', '/today')],
    loadComponent: () =>
      import('./tools/aero-testing/session-detail/session-detail.component')
        .then((m) => m.SessionDetailComponent),
  },
  {
    path: 'tools/pm-validation',
    canActivate: [featureGuard('tools.pm_validation', '/today')],
    loadComponent: () =>
      import('./tools/pm-validation/pm-validation.component').then((m) => m.PmValidationComponent),
  },
  // Bahn-Wettkaempfe (13.10.2026): Wettkampf -> Disziplinen -> Event-Seite der
  // Disziplin. Die alten Event-Pfade der beiden Tools leiten hierher um.
  {
    path: 'tools/track-competitions',
    canActivate: [featureGuard('tools.track_competitions', '/today')],
    loadComponent: () =>
      import('./tools/track-competitions/track-competitions.component').then((m) => m.TrackCompetitionsComponent),
  },
  // Sprint (Einzelsprint, 15.10.2026): Disziplin-Seite + Analyse ueber Wettkaempfe
  {
    path: 'tools/track-competitions/sprint-analyse',
    canActivate: [featureGuard('tools.track_competitions', '/today')],
    loadComponent: () =>
      import('./tools/track-competitions/sprint/sprint-analysis-page.component')
        .then((m) => m.SprintAnalysisPageComponent),
  },
  // Altersstruktur der Weltmeisterschaften (20.10.2026, Variante A)
  {
    path: 'tools/track-competitions/altersstruktur',
    canActivate: [featureGuard('tools.track_competitions', '/today')],
    loadComponent: () =>
      import('./tools/track-competitions/age-structure/age-structure-page.component')
        .then((m) => m.AgeStructurePageComponent),
  },
  // Laenderwertung der Weltmeisterschaften (23.10.2026, Variante B + Details C)
  {
    path: 'tools/track-competitions/laenderwertung',
    canActivate: [featureGuard('tools.track_competitions', '/today')],
    loadComponent: () =>
      import('./tools/track-competitions/nation-ranking/nation-ranking-page.component')
        .then((m) => m.NationRankingPageComponent),
  },
  // Leistungsentwicklung (24.10.2026, vorher "Siegerzeiten"): Reiter-Huelle, je Reiter eine Kind-Route
  // (Reihenfolge = PERFORMANCE_TABS). Siegerzeiten WM + OS mit Olympia-Prognose (23.10.2026, Variante C),
  // Massenstart (Durchschnittstempo) + MV im Vergleich (24.10.2026, Variante C + Rechner aus B), Rueckstand Deutschland (24.10.2026, Variante B).
  { path: 'tools/track-competitions/siegerzeiten', redirectTo: 'tools/track-competitions/leistungsentwicklung/siegerzeiten', pathMatch: 'full' },
  {
    path: 'tools/track-competitions/leistungsentwicklung',
    canActivate: [featureGuard('tools.track_competitions', '/today')],
    loadComponent: () =>
      import('./tools/track-competitions/performance/performance-page.component')
        .then((m) => m.PerformancePageComponent),
    children: [
      { path: '', redirectTo: 'siegerzeiten', pathMatch: 'full' },
      {
        path: 'siegerzeiten',
        loadComponent: () =>
          import('./tools/track-competitions/winning-times/winning-times-page.component')
            .then((m) => m.WinningTimesPageComponent),
      },
      {
        path: 'massenstart',
        loadComponent: () =>
          import('./tools/track-competitions/mass-start/mass-start-page.component')
            .then((m) => m.MassStartPageComponent),
      },
      {
        path: 'mv-vergleich',
        loadComponent: () =>
          import('./tools/track-competitions/performance-comparison/performance-comparison-page.component')
            .then((m) => m.PerformanceComparisonPageComponent),
      },
      {
        path: 'rueckstand-deutschland',
        loadComponent: () =>
          import('./tools/track-competitions/nation-gap/nation-gap-page.component')
            .then((m) => m.NationGapPageComponent),
      },
    ],
  },
  {
    path: 'tools/track-competitions/sprint/:eventId',
    canActivate: [featureGuard('tools.track_competitions', '/today')],
    loadComponent: () =>
      import('./tools/track-competitions/sprint/sprint-event-detail.component')
        .then((m) => m.SprintEventDetailComponent),
  },
  // Head-to-Head der Laeufe (16.10.2026, ?a=run:12&b=run:34..&h=) -- vor ':eventId',
  // sonst wuerde 'vergleich' als Event-ID gelesen.
  {
    path: 'tools/track-competitions/time-trial/vergleich',
    canActivate: [featureGuard('tools.track_competitions', '/today')],
    data: { h2h: 'time_trial' },
    loadComponent: () =>
      import('./tools/team-pursuit/head-to-head/head-to-head-page.component')
        .then((m) => m.HeadToHeadPageComponent),
  },
  {
    path: 'tools/track-competitions/individual-pursuit/vergleich',
    canActivate: [featureGuard('tools.track_competitions', '/today')],
    data: { h2h: 'individual_pursuit' },
    loadComponent: () =>
      import('./tools/team-pursuit/head-to-head/head-to-head-page.component')
        .then((m) => m.HeadToHeadPageComponent),
  },
  // 1 km Zeitfahren + Einerverfolgung (16.10.2026): eine Seite fuer beide, Zeiten aus der Race Analysis
  {
    path: 'tools/track-competitions/time-trial/:eventId',
    canActivate: [featureGuard('tools.track_competitions', '/today')],
    loadComponent: () =>
      import('./tools/track-competitions/individual/individual-event-detail.component')
        .then((m) => m.IndividualEventDetailComponent),
  },
  {
    path: 'tools/track-competitions/individual-pursuit/:eventId',
    canActivate: [featureGuard('tools.track_competitions', '/today')],
    loadComponent: () =>
      import('./tools/track-competitions/individual/individual-event-detail.component')
        .then((m) => m.IndividualEventDetailComponent),
  },
  // Keirin, Scratch, Punktefahren, Ausscheidung, Omnium, Madison (20.10.2026): eine Seite fuer alle sechs,
  // Daten nur aus dem Result Book der Wettkampfseite
  ...BUNCH_SEGMENTS.map((segment) => ({
    path: `tools/track-competitions/${segment}/:eventId`,
    canActivate: [featureGuard('tools.track_competitions', '/today')],
    loadComponent: () =>
      import('./tools/track-competitions/bunch/bunch-event-detail.component')
        .then((m) => m.BunchEventDetailComponent),
  })),
  {
    path: 'tools/track-competitions/team-pursuit/:eventId',
    canActivate: [featureGuard('tools.track_competitions', '/today')],
    loadComponent: () =>
      import('./tools/track-competitions/team-pursuit/pursuit-event-detail/pursuit-event-detail.component')
        .then((m) => m.PursuitEventDetailComponent),
  },
  {
    path: 'tools/track-competitions/team-sprint/:eventId',
    canActivate: [featureGuard('tools.track_competitions', '/today')],
    loadComponent: () =>
      import('./tools/track-competitions/team-sprint/event-detail/event-detail.component')
        .then((m) => m.EventDetailComponent),
  },
  // Wettkampf-Berichte (24.10.2026): Vorlagen, Textbausteine, Editor -- nur Bearbeiter
  {
    path: 'tools/track-competitions/berichte/vorlagen',
    canActivate: [featureGuard('tools.track_competitions.edit', '/tools/track-competitions')],
    loadComponent: () =>
      import('./tools/track-competitions/report/report-templates-page.component')
        .then((m) => m.ReportTemplatesPageComponent),
  },
  {
    path: 'tools/track-competitions/berichte/bausteine',
    canActivate: [featureGuard('tools.track_competitions.edit', '/tools/track-competitions')],
    loadComponent: () =>
      import('./tools/track-competitions/report/report-snippets-page.component')
        .then((m) => m.ReportSnippetsPageComponent),
  },
  {
    path: 'tools/track-competitions/:competitionId/bericht/:reportId',
    canActivate: [featureGuard('tools.track_competitions.edit', '/tools/track-competitions')],
    loadComponent: () =>
      import('./tools/track-competitions/report/report-editor.component')
        .then((m) => m.ReportEditorComponent),
  },
  {
    path: 'tools/track-competitions/:competitionId',
    canActivate: [featureGuard('tools.track_competitions', '/today')],
    loadComponent: () =>
      import('./tools/track-competitions/competition-detail/competition-detail.component')
        .then((m) => m.CompetitionDetailComponent),
  },
  { path: 'tools/team-pursuit/event/:eventId', redirectTo: 'tools/track-competitions/team-pursuit/:eventId' },
  { path: 'tools/team-sprint/:eventId', redirectTo: 'tools/track-competitions/team-sprint/:eventId' },
  {
    // Head-to-head of two Wegzeitverlaeufe (?a=effort:12&b=heat:34) -- before
    // ':effortId', or 'vergleich' would be read as an effort id.
    path: 'tools/team-pursuit/vergleich',
    canActivate: [featureGuard('tools.team_pursuit', '/today')],
    loadComponent: () =>
      import('./tools/team-pursuit/head-to-head/head-to-head-page.component')
        .then((m) => m.HeadToHeadPageComponent),
  },
  {
    path: 'tools/team-pursuit/:effortId',
    canActivate: [featureGuard('tools.team_pursuit', '/today')],
    loadComponent: () =>
      import('./tools/team-pursuit/effort-detail/effort-detail.component').then((m) => m.EffortDetailComponent),
  },
  {
    path: 'tools/team-sprint',
    canActivate: [featureGuard('tools.team_sprint', '/today')],
    loadComponent: () =>
      import('./tools/team-sprint/team-sprint.component').then((m) => m.TeamSprintComponent),
  },
  { path: 'dashboard', redirectTo: 'today', pathMatch: 'full' },
  { path: 'calendar', redirectTo: 'planen/kalender', pathMatch: 'full' },
  { path: 'analytics', redirectTo: 'analyse/trends', pathMatch: 'full' },
  { path: 'training-plans', redirectTo: 'planen/plans', pathMatch: 'full' },
  { path: 'training-plans/plans', redirectTo: 'planen/plans', pathMatch: 'full' },
  { path: 'training-plans/builder', redirectTo: 'planen/builder', pathMatch: 'full' },
  { path: 'training-plans/saison', redirectTo: 'planen/saison', pathMatch: 'full' },
  { path: 'training-plans/library', redirectTo: 'planen/bibliothek', pathMatch: 'full' },
  { path: 'training-analysis', redirectTo: 'analyse/detail', pathMatch: 'full' },
  { path: 'training-analysis/:id', redirectTo: 'analyse/detail/:id', pathMatch: 'full' },
  { path: 'report', redirectTo: 'analyse/reports', pathMatch: 'full' },
  { path: 'user_settings', redirectTo: 'profil/einstellungen', pathMatch: 'full' },
  { path: 'integrations', redirectTo: 'profil/integrationen', pathMatch: 'full' },
  { path: '**', redirectTo: 'today' },
];

export const appRoutes: Routes = [
  {
    path: 'login',
    loadComponent: () =>
      import('./auth/login/login.component').then((m) => m.LoginComponent),
  },
  {
    path: 'signup',
    loadComponent: () =>
      import('./auth/signup/signup.component').then((m) => m.SignupComponent),
  },
  {
    path: 'claim/:token',
    loadComponent: () =>
      import('./auth/claim/claim.component').then((m) => m.ClaimComponent),
  },
  {
    path: 'forgot-password',
    loadComponent: () =>
      import('./auth/forgot-password/forgot-password.component').then((m) => m.ForgotPasswordComponent),
  },
  {
    path: 'reset-password/:token',
    loadComponent: () =>
      import('./auth/reset-password/reset-password.component').then((m) => m.ResetPasswordComponent),
  },
  {
    // Ohne authGuard: Der Link wird im NEUEN Postfach geklickt, womöglich in
    // einem Browser ohne Session. Der Token allein autorisiert.
    path: 'confirm-email/:token',
    loadComponent: () =>
      import('./auth/confirm-email/confirm-email.component').then((m) => m.ConfirmEmailComponent),
  },
  {
    path: 'report/preview',
    loadComponent: () =>
      import('./reporting/report-preview-page.component').then((m) => m.ReportPreviewPageComponent),
  },
  {
    // Druckseite des Entwicklungsberichts fuer das Server-PDF / die Monatsmail
    // (Playwright). Wie report/preview ausserhalb des Auth-Layouts: der Server
    // legt Token und currentAthleteId in den localStorage. Liest ?athlete=&month=,
    // setzt .report-ready-signal erst, wenn alle Diagramme fertig sind - und nie,
    // wenn der geladene Bericht zu einem anderen Sportler gehoert.
    path: 'report/entwicklung/druck',
    data: { print: true },
    loadComponent: () =>
      import('./analytics/development-report/development-report.component').then((m) => m.DevelopmentReportComponent),
  },
  {
    // Druckseite eines Wettkampf-Berichts fuer das Server-PDF (Playwright, 24.10.2026). Wie die
    // Entwicklungsbericht-Druckseite ausserhalb des Auth-Layouts: der Server legt den Token in den
    // localStorage. Liest ?report=, setzt .report-ready-signal erst, wenn Bericht + Wettkampf
    // zusammenpassen und alle Diagramme fertig sind, sonst .report-error-signal.
    path: 'report/wettkampf/druck',
    loadComponent: () =>
      import('./tools/track-competitions/report/report-print.component').then((m) => m.ReportPrintComponent),
  },
  {
    path: 'dev/design-system',
    loadComponent: () =>
      import('./dev/design-system/design-system.component').then((m) => m.DevDesignSystemComponent),
  },
  {
    path: '',
    canActivate: [authGuard],
    children: [
      {
        path: '',
        canMatch: [coachMatchGuard],
        canActivate: [coachGuard],
        loadComponent: () =>
          import('./layout/main-layout.component').then((m) => m.MainLayoutComponent),
        children: coachChildren,
      },
      {
        path: '',
        canMatch: [athleteMatchGuard],
        canActivate: [athleteGuard],
        loadComponent: () =>
          import('./layout/athlete-layout.component').then((m) => m.AthleteLayoutComponent),
        children: athleteChildren,
      },
      {
        // Eingeschränkter Account-Typ: eigener, schmaler Bereich (nur freigegebene Daten).
        // Kinder statt einer einzelnen Komponente, damit Staff überhaupt an
        // eine Kontoseite kommt — /account liegt in den Coach-/Athleten-Bäumen
        // und ist für diese Rolle nicht erreichbar.
        path: 'staff',
        canMatch: [staffMatchGuard],
        canActivate: [staffGuard],
        children: [
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () =>
              import('./staff/staff-dashboard.component').then((m) => m.StaffDashboardComponent),
          },
          {
            path: 'account',
            loadComponent: () =>
              import('./account/account.component').then((m) => m.AccountComponent),
          },
        ],
      },
    ],
  },
  { path: '**', redirectTo: 'login' },
];
